<template>
  <div class="p-4 space-y-4">
    <LeaveAdminSectionNav />

    <el-card shadow="hover" class="rounded-2xl border border-gray-200">
      <div class="text-base font-semibold mb-4">휴가유형 관리</div>

      <el-table :data="leaveTypeRows || []" size="small" style="width: 100%" v-loading="leaveTypeLoading">
        <el-table-column prop="name" label="유형명" min-width="140" />
        <el-table-column prop="default_quota" label="기본일수" width="100" />
        <el-table-column label="이월가능" width="100">
          <template #default="{ row }">
            <el-tag :type="row.carry_forward ? 'success' : 'info'" size="small">
              {{ row.carry_forward ? '예' : '아니오' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="증빙 첨부" width="110">
          <template #default="{ row }">
            <el-tag :type="row.requires_attachment ? 'warning' : 'info'" size="small">
              {{ row.requires_attachment ? '필수' : '불필요' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="attachment_guide" label="증빙 안내" min-width="200" />
        <el-table-column prop="description" label="설명" min-width="220" />
        <el-table-column label="관리" width="160">
          <template #default="{ row }">
            <el-button size="small" @click="startEditLeaveType(row)">편집</el-button>
            <el-button size="small" type="danger" @click="deleteLeaveType(row)">삭제</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="mt-4 border-t pt-4">
        <div class="text-sm font-semibold mb-3">{{ leaveTypeForm.id ? '휴가유형 수정' : '휴가유형 추가' }}</div>
        <el-form :model="leaveTypeForm" label-width="90px">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
            <el-form-item label="유형명" required>
              <el-input v-model="leaveTypeForm.name" placeholder="예: 연차" />
            </el-form-item>
            <el-form-item label="기본일수">
              <el-input-number
                v-model="leaveTypeForm.default_quota"
                :min="0"
                :max="365"
                :step="0.5"
                :precision="1"
                style="width: 100%"
              />
            </el-form-item>
            <el-form-item label="이월가능">
              <el-switch v-model="leaveTypeForm.carry_forward" />
            </el-form-item>
            <!-- 증빙 첨부 필수 여부 — 켜면 해당 유형은 첨부 없이 신청이 불가하다.
                 코드 수정/배포 없이 인사담당자가 여기서 관리한다. -->
            <el-form-item label="증빙 필수">
              <el-switch v-model="leaveTypeForm.requires_attachment" />
            </el-form-item>
          </div>
          <el-form-item label="증빙 안내" v-if="leaveTypeForm.requires_attachment">
            <el-input
              v-model="leaveTypeForm.attachment_guide"
              maxlength="200"
              show-word-limit
              placeholder="예: 무급휴가 신청서(서명본) 스캔본을 첨부하세요."
            />
          </el-form-item>
          <el-form-item label="설명">
            <el-input v-model="leaveTypeForm.description" type="textarea" :rows="2" placeholder="설명" />
          </el-form-item>
        </el-form>
        <div class="flex gap-2 justify-end">
          <el-button @click="resetLeaveTypeForm">초기화</el-button>
          <el-button type="primary" @click="saveLeaveType" :loading="leaveTypeSaving">저장</el-button>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import axiosInstance from '@/api/axios'
import LeaveAdminSectionNav from '@/components/LeaveAdminSectionNav.vue'

const leaveTypeLoading = ref(false)
const leaveTypeSaving = ref(false)
const leaveTypeRows = ref([])
const leaveTypeForm = ref({
  id: null,
  name: '',
  default_quota: 0,
  carry_forward: false,
  requires_attachment: false,
  attachment_guide: '',
  description: '',
})

const normalizeListResponse = (data) => {
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.results)) return data.results
  return []
}

const extractApiErrorMessage = (err, fallback) => {
  const data = err?.response?.data
  if (!data) return fallback
  if (typeof data === 'string') return data
  if (data.detail) return data.detail
  const firstKey = Object.keys(data)[0]
  if (!firstKey) return fallback
  const value = data[firstKey]
  return Array.isArray(value) ? value[0] : value || fallback
}

const resetLeaveTypeForm = () => {
  leaveTypeForm.value = {
    id: null,
    name: '',
    default_quota: 0,
    carry_forward: false,
    requires_attachment: false,
    attachment_guide: '',
    description: '',
  }
}

const fetchLeaveTypes = async () => {
  leaveTypeLoading.value = true
  try {
    const res = await axiosInstance.get('leave-types/')
    leaveTypeRows.value = normalizeListResponse(res.data)
  } catch (err) {
    ElMessage.error(extractApiErrorMessage(err, '휴가유형 목록 조회에 실패했습니다.'))
    leaveTypeRows.value = []
  } finally {
    leaveTypeLoading.value = false
  }
}

const startEditLeaveType = (row) => {
  leaveTypeForm.value = {
    id: row.id,
    name: row.name ?? '',
    default_quota: Number(row.default_quota || 0),
    carry_forward: !!row.carry_forward,
    requires_attachment: !!row.requires_attachment,
    attachment_guide: row.attachment_guide ?? '',
    description: row.description ?? '',
  }
}

const saveLeaveType = async () => {
  if (!leaveTypeForm.value.name?.trim()) {
    ElMessage.warning('유형명을 입력해 주세요.')
    return
  }

  const payload = {
    name: leaveTypeForm.value.name.trim(),
    default_quota: Number(leaveTypeForm.value.default_quota || 0),
    carry_forward: !!leaveTypeForm.value.carry_forward,
    requires_attachment: !!leaveTypeForm.value.requires_attachment,
    attachment_guide: leaveTypeForm.value.attachment_guide?.trim() || '',
  }

  leaveTypeSaving.value = true
  try {
    if (leaveTypeForm.value.id) {
      await axiosInstance.patch(`leave-types/${leaveTypeForm.value.id}/`, payload)
      ElMessage.success('휴가유형을 수정했습니다.')
    } else {
      await axiosInstance.post('leave-types/', payload)
      ElMessage.success('휴가유형을 추가했습니다.')
    }

    await fetchLeaveTypes()
    resetLeaveTypeForm()
  } catch (err) {
    ElMessage.error(extractApiErrorMessage(err, '휴가유형 저장에 실패했습니다.'))
  } finally {
    leaveTypeSaving.value = false
  }
}

const deleteLeaveType = async (row) => {
  try {
    await ElMessageBox.confirm(
      `[${row.name}] 유형을 삭제하시겠습니까?`,
      '휴가유형 삭제',
      {
        type: 'warning',
        confirmButtonText: '삭제',
        cancelButtonText: '취소',
      },
    )
  } catch {
    return
  }

  try {
    await axiosInstance.delete(`leave-types/${row.id}/`)
    if (leaveTypeForm.value.id === row.id) {
      resetLeaveTypeForm()
    }
    await fetchLeaveTypes()
    ElMessage.success('휴가유형을 삭제했습니다.')
  } catch (err) {
    ElMessage.error(extractApiErrorMessage(err, '휴가유형 삭제에 실패했습니다.'))
  }
}

onMounted(async () => {
  await fetchLeaveTypes()
})
</script>
